import { useState } from "react";
import { toast } from "sonner";
import { Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { BrandButton } from "@/components/PageHeader";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const UNDO_ROLES = ["Super Admin", "Event Manager", "Accounts Officer", "Accountant", "Financial Secretary"];
const COPY = {
  kit: { label: "Undo kit issue", title: "Undo badge & kit issue", note: "The kit will show as not issued, and the participant gets an SMS saying the souvenir record was reversed." },
  checkin: { label: "Undo check-in", title: "Undo check-in", note: "The participant will show as not checked in for this event and can be scanned again." },
};

export const useCanUndoGate = () => UNDO_ROLES.includes(useApp().role);

export const GateUndoButton = ({ txnId, what, onDone, testId }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const c = COPY[what];
  const go = async () => {
    if (reason.trim().length < 3) { toast.error("Please give the reason for the undo"); return; }
    setBusy(true);
    try {
      const r = await api.post("/gate/undo", { txn_id: txnId, what, reason });
      toast.success(what === "kit" ? "Kit issue undone. The participant will be notified by SMS" : "Check-in undone");
      setOpen(false); setReason("");
      onDone?.(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="text-xs font-semibold text-gray-600 hover:text-[#E60000] hover:underline inline-flex items-center gap-1" data-testid={testId}><Undo2 className="w-3.5 h-3.5" />{c.label}</button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle>{c.title}</DialogTitle><DialogDescription>{c.note}</DialogDescription></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Reason, e.g. scanned the wrong badge" className="min-h-[80px]" data-testid={`${testId}-reason`} />
          <div className="flex justify-end gap-2">
            <BrandButton variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid={`${testId}-cancel`}>Cancel</BrandButton>
            <BrandButton onClick={go} disabled={busy} data-testid={`${testId}-confirm`}>{busy ? "Saving…" : c.label}</BrandButton>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const GateUndoActions = ({ txnId, checkedIn, kitIssued, onDone, prefix }) => {
  const can = useCanUndoGate();
  if (!can || !txnId || !checkedIn) { return null; }
  return (
    <div className="flex flex-wrap gap-3 py-2" data-testid={`${prefix}-undo-actions`}>
      {kitIssued ? <GateUndoButton txnId={txnId} what="kit" onDone={onDone} testId={`${prefix}-undo-kit`} /> : <GateUndoButton txnId={txnId} what="checkin" onDone={onDone} testId={`${prefix}-undo-checkin`} />}
    </div>
  );
};
